import { useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ONE_HOUR_MS, STALE_TIME } from '@/shared/constants/app/time.constant.ts';

import { docsApi } from './docs.api.ts';
import { docsKeys } from './docs.keys.ts';

/**
 * 도움말 트리.
 *
 * 사이드바·인덱스·검색·이전다음이 전부 이 하나를 본다. 문서 사이를 오갈 때
 * 다시 받지 않도록 staleTime 을 길게 잡는다 — 도움말은 분 단위로 바뀌지 않는다.
 *
 * `manage=true` 는 초안까지 포함한 관리용 경로를 부른다(ADMIN 전용).
 */
export function useDocsTree(manage = false) {
  return useQuery({
    queryKey: docsKeys.tree(manage),
    queryFn: () => docsApi.getTree(manage),
    staleTime: STALE_TIME.LONG,
    gcTime: ONE_HOUR_MS,
  });
}

/** 문서 본문. 한 번 읽은 문서는 뒤로 가기에서 즉시 뜬다. */
export function useDocArticle(
  section: string | undefined,
  slug: string | undefined,
  manage = false,
) {
  return useQuery({
    queryKey: docsKeys.article(section ?? '', slug ?? '', manage),
    queryFn: () => docsApi.getArticle(section!, slug!, manage),
    enabled: !!section && !!slug,
    staleTime: STALE_TIME.LONG,
    gcTime: ONE_HOUR_MS,
    // 404(없는 문서)는 재시도할 이유가 없다 — 리다이렉트 판정이 그만큼 늦어진다.
    retry: false,
  });
}

/** 포인터가 링크 위에 머물렀다고 볼 시간. 스쳐 지나간 링크는 받지 않는다. */
const HOVER_INTENT_MS = 120;

/**
 * 링크에 **머무를 때** 본문을 미리 받아둔다.
 *
 * SPA 라 본문 요청은 클릭한 뒤에야 나가고 그 왕복이 그대로 빈 화면이 된다.
 * 다만 `mouseenter` 즉시 받으면 사이드바를 아래로 훑는 동작 한 번에 지나친 링크
 * 수만큼(수십 건) 본문 요청이 한꺼번에 나간다 — 키보드 Tab 이동은 더 심하다.
 * 그래서 잠깐 머문 뒤에만 당겨오고, 벗어나면 취소한다.
 *
 * 이미 캐시에 fresh 로 있으면 react-query 가 요청을 건너뛴다.
 */
export function useDocPrefetch(manage = false) {
  const queryClient = useQueryClient();
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const cancel = useCallback(() => {
    if (timerRef.current !== undefined) clearTimeout(timerRef.current);
    timerRef.current = undefined;
  }, []);

  // 언마운트 시 남은 타이머가 없어야 한다(사라진 화면의 요청을 쏘지 않게).
  useEffect(() => cancel, [cancel]);

  const schedule = useCallback(
    (section: string, slug: string) => {
      cancel();
      timerRef.current = setTimeout(() => {
        void queryClient.prefetchQuery({
          queryKey: docsKeys.article(section, slug, manage),
          queryFn: () => docsApi.getArticle(section, slug, manage),
          staleTime: STALE_TIME.LONG,
        });
      }, HOVER_INTENT_MS);
    },
    [cancel, queryClient, manage],
  );

  /** 링크에 그대로 펼쳐 넣는다: `{...prefetchProps(section, slug)}` */
  return useCallback(
    (section: string, slug: string) => ({
      onMouseEnter: () => schedule(section, slug),
      onFocus: () => schedule(section, slug),
      onMouseLeave: cancel,
      onBlur: cancel,
    }),
    [schedule, cancel],
  );
}
